import { ResultTypeFrom } from '@reduxjs/toolkit/query';
import type { EndpointDataProviderPaginator } from '../types';
import type { AnyQueryDefinition } from '../../apiSSRModule';
import { mapRowToChunkIndex } from '../lib/mapRowToChunkIndex/mapRowToChunkIndex';

/**
 * Параметры для конфигурации пагинатора
 */
export type LimitOffsetPaginatorConfig<
    Limit extends number = number,
    Definition extends AnyQueryDefinition = AnyQueryDefinition,
> = {
    /** Количество запрашиваемых строк для одного запроса */
    limit: Limit;
    /** Признак наличия данных для загрузки */
    hasMore?(chunkData: ResultTypeFrom<Definition>): boolean;
};

/**
 * Параметры пагинатора для оперирования данными
 */
export type LimitOffsetPaginatorParams<Limit extends number = number> = {
    /** Смещение в данных */
    offset: number;
    /** Размер страницы данных */
    limit: Limit;
};

/**
 * Реализация базовой пагинации с помощью параметров offset и limit.
 */
export function createLimitOffsetPaginator<
    Limit extends number = number,
    Definition extends AnyQueryDefinition = AnyQueryDefinition,
>({
    limit,
    hasMore,
}: LimitOffsetPaginatorConfig<Limit, Definition>): EndpointDataProviderPaginator<
    Definition,
    LimitOffsetPaginatorParams<Limit>
> {
    return {
        getLimit() {
            return limit;
        },
        getChunkParams(rowIndex: number) {
            const chunkIndex = mapRowToChunkIndex(rowIndex, limit);

            return { offset: chunkIndex * limit, limit };
        },
        getRowIndexInChunk(rowIndex: number, chunkParams: LimitOffsetPaginatorParams) {
            return rowIndex - chunkParams.offset;
        },
        hasMore,
    };
}
